import { cn } from '@invent/wl-ui-kit-next';
import { Button, Textarea, Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { internalFieldsApi } from '../api/internal-fields-api';
import type { INoteInitials } from '../api/internal-field-contracts';
import { countOf } from '../lists';
import { noteEntries, whoWrote, type INoteEntry } from '../notes-log';
import { refusalMessage } from '../refusal';
import { formatDay } from './days';

/**
 * A survey's notes log drawn as its entries (`notes-log.ts`), wherever it is read — the
 * Reinstatement row, the record page's Notes field: the latest entry, and the earlier ones on
 * asking, or at once with `startOpen`. **Each entry's initials say who they are on hover**
 * (SR-494): "DF — Dylan Ferrante", "(left)" for somebody no longer at Shook, "possibly" where the
 * research is unsure; a tag nobody could be named for stays a plain tag. The initials are read
 * once per page load and shared (`internalFieldsApi.noteInitials`); until they land, or where the
 * read fails, every tag is plain.
 */
export function NotesLog({ notes, startOpen = false }: { notes: string; startOpen?: boolean }) {
  const entries = React.useMemo(() => noteEntries(notes), [notes]);
  const table = useNoteInitials();
  const [open, setOpen] = React.useState(startOpen);
  const shown = open ? entries : entries.slice(0, 1);
  const earlier = entries.length - 1;
  // At the end of the last line drawn, not a line of its own: a link's colour and underline, so it
  // reads as something to press, and a button, as it opens and shuts.
  const toggle = earlier > 0 && (
    <>
      {' · '}
      <button
        type="button"
        className={cn(
          'text-xs font-medium whitespace-nowrap underline underline-offset-4',
          'text-[var(--color-sapphire)] hover:text-[var(--color-royal-blue)]'
        )}
        aria-expanded={open}
        aria-label={open ? 'Show the latest only' : `Show ${countOf(earlier, 'earlier entry', 'earlier entries')}`}
        onClick={() => setOpen(!open)}
      >
        {open ? 'less' : `+${earlier} earlier`}
      </button>
    </>
  );
  return (
    <ol data-notes="" className="flex flex-col gap-1.5">
      {shown.map((entry, at) => (
        <li key={at} className="whitespace-pre-wrap">
          {entry.day && <span className="font-medium tabular-nums">{formatDay(entry.day)}</span>}
          {entry.initials && <InitialsTag entry={entry} table={table} />}
          {(entry.day || entry.initials) && ' — '}
          {entry.text}
          {at === shown.length - 1 && toggle}
        </li>
      ))}
    </ol>
  );
}

/** Who an entry's author was, in words — "Dylan Ferrante", "possibly Chiara Goodman (left)". */
function authorWords(author: INoteInitials): string {
  return `${author.confidence === 'low' ? 'possibly ' : ''}${author.name}${author.left ? ' (left)' : ''}`;
}

/** An entry's initials as a tag, and on hover whom they stand for that day. */
function InitialsTag({ entry, table }: { entry: INoteEntry; table: readonly INoteInitials[] }) {
  const author = whoWrote(entry, table);
  const tag = (
    <span
      data-initials={entry.initials ?? undefined}
      role={author ? 'img' : undefined}
      tabIndex={author ? 0 : undefined}
      aria-label={author ? `${entry.initials} — ${authorWords(author)}` : undefined}
      className={cn(
        'ml-1.5 rounded bg-muted px-1 text-xs text-muted-foreground',
        author && 'cursor-help underline decoration-dotted underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      {entry.initials}
    </span>
  );
  if (!author) return tag;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{tag}</TooltipTrigger>
      <TooltipContent className="grid gap-0.5">
        <span>
          {entry.initials} — {authorWords(author)}
        </span>
        {author.email && <span className="text-xs opacity-80">{author.email}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

/** The name the initials table gives an address — a staff note's author (SR-494) — or null. */
export function nameOfEmail(table: readonly INoteInitials[], email: string): Nullable<string> {
  const wanted = email.toLowerCase();
  return table.find((row) => row.name !== null && row.email?.toLowerCase() === wanted)?.name ?? null;
}

/** The initials table, once read — empty until then and where the read fails. */
export function useNoteInitials(): readonly INoteInitials[] {
  const [table, setTable] = React.useState<readonly INoteInitials[]>([]);
  React.useEffect(() => {
    let live = true;
    internalFieldsApi.noteInitials().then(
      (rows) => live && setTable(rows),
      () => undefined
    );
    return () => {
      live = false;
    };
  }, []);
  return table;
}

/**
 * *Add entry* under a notes log (SR-494): the text alone — survey-api dates it and signs it with
 * the signed-in account's initials, and puts it at the log's head, so a new entry reads as the
 * migrated ones do and its tag says who on hover. Typed initials are no longer the signature;
 * the field's own pencil still edits the log as text.
 */
export function LogEntryComposer({
  append,
  notPermitted
}: {
  /** Writes the entry; a refusal is thrown and said here. */
  append: (text: string) => Promise<unknown>;
  /** The id of the `NotPermitted` sentence that draws the press off. */
  notPermitted?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);

  if (!open) {
    return (
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto justify-self-start p-0 text-xs"
        disabled={notPermitted !== undefined}
        aria-describedby={notPermitted}
        onClick={() => setOpen(true)}
      >
        Add entry
      </Button>
    );
  }
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setSaid(null);
    try {
      await append(text.trim());
      setText('');
      setOpen(false);
    } catch (error) {
      setSaid(refusalMessage(error, 'The entry could not be added.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="grid gap-2" onSubmit={submit}>
      <Textarea
        autoFocus
        rows={2}
        aria-label="New entry"
        placeholder="Dated and signed with your initials"
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      {said && (
        <p role="alert" className="text-xs text-destructive">
          {said}
        </p>
      )}
      <span className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={busy || text.trim() === ''}>
          {busy ? 'Adding…' : 'Add entry'}
        </Button>
      </span>
    </form>
  );
}
